import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, AlertTriangle, Banknote, Check, CreditCard, Lock, MapPin, Phone, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { api } from '../App';

// Simple math CAPTCHA generator
const generateCaptcha = () => {
  const a = Math.floor(Math.random() * 10) + 1;
  const b = Math.floor(Math.random() * 10) + 1;
  return { question: `${a} + ${b}`, answer: String(a + b) };
};

// Card brand SVG logos
const VisaLogo = () => (
  <svg viewBox="0 0 48 32" className="h-8 w-auto" aria-label="Visa">
    <rect width="48" height="32" rx="4" fill="#1A1F71" />
    <text x="24" y="20" textAnchor="middle" fill="#FFFFFF" fontSize="12" fontWeight="bold" fontFamily="Arial, sans-serif">VISA</text>
  </svg>
);

const MastercardLogo = () => (
  <svg viewBox="0 0 48 32" className="h-8 w-auto" aria-label="Mastercard">
    <rect width="48" height="32" rx="4" fill="#252525" />
    <circle cx="19" cy="16" r="9" fill="#EB001B" />
    <circle cx="29" cy="16" r="9" fill="#F79E1B" />
    <path d="M24 9.5a9 9 0 0 1 0 13" fill="#FF5F00" />
    <path d="M24 9.5a9 9 0 0 0 0 13" fill="#FF5F00" />
  </svg>
);

const CheckoutPage = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [paymentMode, setPaymentMode] = useState('cash_on_arrival');

  // Cancellation policy acknowledgement
  const [policyAcknowledged, setPolicyAcknowledged] = useState(false);

  // CAPTCHA
  const [captcha, setCaptcha] = useState(generateCaptcha());
  const [captchaInput, setCaptchaInput] = useState('');
  const [captchaVerified, setCaptchaVerified] = useState(false);

  const refreshCaptcha = useCallback(() => {
    setCaptcha(generateCaptcha());
    setCaptchaInput('');
    setCaptchaVerified(false);
  }, []);

  const verifyCaptcha = useCallback((value) => {
    setCaptchaInput(value);
    setCaptchaVerified(value === captcha.answer);
  }, [captcha.answer]);

  const [cardData, setCardData] = useState({
    cardNumber: '',
    cardholderName: '',
    expMonth: '',
    expYear: '',
    cvv: ''
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [bookingRes, settingsRes] = await Promise.all([
          api.get(`/bookings/${bookingId}`),
          api.get('/settings/payment-mode')
        ]);
        
        setBooking(bookingRes.data);
        setPaymentMode(settingsRes.data.payment_mode || 'cash_on_arrival');
        
        if (bookingRes.data.payment_status === 'fully_paid' || bookingRes.data.payment_status === 'pay_on_arrival') {
          navigate(`/confirmation/${bookingId}`);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Booking not found');
        navigate('/');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [bookingId, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'cardNumber') {
      const cleaned = value.replace(/\D/g, '').slice(0, 16);
      const formatted = cleaned.replace(/(\d{4})(?=\d)/g, '$1 ');
      setCardData(prev => ({ ...prev, [name]: formatted }));
      return;
    }
    if (name === 'expMonth') {
      setCardData(prev => ({ ...prev, [name]: value.replace(/\D/g, '').slice(0, 2) }));
      return;
    }
    if (name === 'expYear') {
      setCardData(prev => ({ ...prev, [name]: value.replace(/\D/g, '').slice(0, 4) }));
      return;
    }
    if (name === 'cvv') {
      setCardData(prev => ({ ...prev, [name]: value.replace(/\D/g, '').slice(0, 4) }));
      return;
    }
    setCardData(prev => ({ ...prev, [name]: value }));
  };

  const canSubmit = policyAcknowledged && captchaVerified;

  const handleCashOnArrival = async () => {
    if (!canSubmit) {
      if (!policyAcknowledged) toast.error('Please acknowledge the cancellation policy');
      if (!captchaVerified) toast.error('Please complete the security verification');
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/bookings/${bookingId}/confirm-cash`);
      toast.success('Booking confirmed! Pay on arrival.');
      navigate(`/confirmation/${bookingId}`);
    } catch (error) {
      console.error('Confirm error:', error);
      toast.error(error.response?.data?.detail || 'Failed to confirm booking. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCardPayment = async (e) => {
    e.preventDefault();
    if (!canSubmit) {
      if (!policyAcknowledged) toast.error('Please acknowledge the cancellation policy');
      if (!captchaVerified) toast.error('Please complete the security verification');
      return;
    }
    if (!cardData.cardNumber || !cardData.cardholderName || !cardData.expMonth || !cardData.expYear || !cardData.cvv) {
      toast.error('Please fill in all payment details');
      return;
    }
    
    setSubmitting(true);
    try {
      await api.post('/payments/process', {
        booking_id: bookingId,
        amount: booking.total_amount,
        payment_type: 'full',
        card_number: cardData.cardNumber.replace(/\s/g, ''),
        card_exp_month: parseInt(cardData.expMonth),
        card_exp_year: parseInt(cardData.expYear),
        card_cvv: cardData.cvv,
        cardholder_name: cardData.cardholderName
      });
      toast.success('Payment successful!');
      navigate(`/confirmation/${bookingId}`);
    } catch (error) {
      console.error('Payment error:', error);
      toast.error(error.response?.data?.detail || 'Payment failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (!booking) return null;

  const isPaid = booking.payment_status === 'fully_paid' || booking.payment_status === 'pay_on_arrival';
  if (isPaid) {
    navigate(`/confirmation/${bookingId}`);
    return null;
  }

  const isCardEnabled = paymentMode !== 'cash_on_arrival';

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-white border-b border-border">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <Link to={`/tours/${booking.tour_id}`} className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors" data-testid="back-to-tour-link">
            <ArrowLeft className="w-5 h-5" />
            Back to Tour
          </Link>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Booking Summary */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl border border-border p-6 sticky top-8">
              <h2 className="font-serif text-xl font-bold mb-4" data-testid="booking-summary-title">Booking Summary</h2>
              
              <div className="space-y-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Tour</p>
                  <p className="font-semibold" data-testid="booking-tour-title">{booking.tour_title}</p>
                </div>
                
                <div className="flex gap-4">
                  <div>
                    <p className="text-muted-foreground flex items-center gap-1">
                      <Calendar className="w-4 h-4" /> Date
                    </p>
                    <p className="font-semibold" data-testid="booking-date">{booking.date}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground flex items-center gap-1">
                      <Clock className="w-4 h-4" /> Time
                    </p>
                    <p className="font-semibold" data-testid="booking-time">{booking.time_slot}</p>
                  </div>
                </div>
                
                <div>
                  <p className="text-muted-foreground">Guests</p>
                  <p className="font-semibold" data-testid="booking-guests">
                    {booking.adults} Adult{booking.adults !== 1 ? 's' : ''}
                    {booking.children > 0 && `, ${booking.children} Child${booking.children !== 1 ? 'ren' : ''}`}
                  </p>
                </div>

                {booking.add_ons && booking.add_ons.length > 0 && (
                  <div>
                    <p className="text-muted-foreground">Add-ons</p>
                    <ul className="font-semibold">
                      {booking.add_ons.map((addon, idx) => (
                        <li key={idx}>{addon.name} (+BSD ${addon.price})</li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="border-t border-border pt-4">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Total</span>
                    <span className="font-bold text-lg" data-testid="booking-total">BSD ${booking.total_amount.toFixed(2)}</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">Currency: Bahamian Dollar (BSD)</p>
                </div>
              </div>

              {/* Merchant Info (RBC #1, #12) */}
              <div className="mt-6 pt-4 border-t border-border text-xs text-muted-foreground space-y-1" data-testid="merchant-info">
                <p className="font-semibold text-foreground text-sm">Harbor Point Holdings d/b/a The Village</p>
                <p className="flex items-center gap-1"><MapPin className="w-3 h-3" /> Athol Island, Nassau, The Bahamas</p>
                <p className="flex items-center gap-1"><Phone className="w-3 h-3" /> +1 (242) 825-6265</p>
                <p>Daily, 9:00 AM - 5:00 PM (EST)</p>
              </div>
            </div>
          </div>

          {/* Payment Section */}
          <div className="lg:col-span-3 space-y-6">
            <div className="bg-white rounded-2xl border border-border p-6">
              {!isCardEnabled ? (
                <>
                  {/* Cash on Arrival Flow */}
                  <div className="flex items-center gap-2 mb-6">
                    <Banknote className="w-6 h-6 text-primary" />
                    <h2 className="font-serif text-xl font-bold" data-testid="payment-title">Complete Your Booking</h2>
                  </div>

                  <div className="flex items-start gap-3 mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm" data-testid="cash-on-arrival-notice">
                    <AlertTriangle className="w-5 h-5 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-semibold mb-1">Online credit / debit card payments are currently unavailable for The Village.</p>
                      <p>You may proceed with your booking and pay upon arrival. Credit / debit card acceptance is coming soon.</p>
                    </div>
                  </div>

                  <div className="space-y-4 mb-6">
                    <div className="bg-muted/50 rounded-lg p-4">
                      <h3 className="font-semibold mb-3">How it works:</h3>
                      <ul className="space-y-3 text-sm text-muted-foreground">
                        <li className="flex items-start gap-2">
                          <span className="w-6 h-6 bg-primary/10 text-primary rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">1</span>
                          <span>Confirm your booking below to reserve your spot</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="w-6 h-6 bg-primary/10 text-primary rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">2</span>
                          <span>You will receive a booking confirmation with your QR code</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="w-6 h-6 bg-primary/10 text-primary rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">3</span>
                          <span>Pay the full amount of <strong>BSD ${booking.total_amount.toFixed(2)}</strong> in cash upon arrival at check-in</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {/* Card Payment Flow */}
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-2">
                      <CreditCard className="w-6 h-6 text-primary" />
                      <h2 className="font-serif text-xl font-bold" data-testid="payment-title">Payment Details</h2>
                    </div>
                    {/* Card brand logos (RBC #5) */}
                    <div className="flex items-center gap-2" data-testid="card-logos">
                      <VisaLogo />
                      <MastercardLogo />
                    </div>
                  </div>

                  <div className="flex items-center gap-2 mb-6 p-3 bg-green-50 rounded-lg text-green-700 text-sm">
                    <Lock className="w-4 h-4" />
                    Your payment is secured with 256-bit SSL encryption
                  </div>

                  <div className="space-y-6 mb-6">
                    <div>
                      <Label htmlFor="cardholderName">Cardholder Name</Label>
                      <Input
                        id="cardholderName"
                        name="cardholderName"
                        placeholder="John Doe"
                        value={cardData.cardholderName}
                        onChange={handleInputChange}
                        className="mt-1"
                        data-testid="cardholder-name"
                      />
                    </div>

                    <div>
                      <Label htmlFor="cardNumber">Card Number</Label>
                      <Input
                        id="cardNumber"
                        name="cardNumber"
                        placeholder="1234 5678 9012 3456"
                        value={cardData.cardNumber}
                        onChange={handleInputChange}
                        className="mt-1"
                        data-testid="card-number"
                      />
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label htmlFor="expMonth">Month</Label>
                        <Input
                          id="expMonth"
                          name="expMonth"
                          placeholder="MM"
                          value={cardData.expMonth}
                          onChange={handleInputChange}
                          className="mt-1"
                          data-testid="exp-month"
                        />
                      </div>
                      <div>
                        <Label htmlFor="expYear">Year</Label>
                        <Input
                          id="expYear"
                          name="expYear"
                          placeholder="YYYY"
                          value={cardData.expYear}
                          onChange={handleInputChange}
                          className="mt-1"
                          data-testid="exp-year"
                        />
                      </div>
                      <div>
                        <Label htmlFor="cvv">CVV</Label>
                        <Input
                          id="cvv"
                          name="cvv"
                          placeholder="123"
                          type="password"
                          value={cardData.cvv}
                          onChange={handleInputChange}
                          className="mt-1"
                          data-testid="cvv"
                        />
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* Cancellation Policy Acknowledgement (RBC #8) */}
              <div className="border-t border-border pt-5 mb-5">
                <label className="flex items-start gap-3 cursor-pointer" data-testid="policy-acknowledgement">
                  <input
                    type="checkbox"
                    checked={policyAcknowledged}
                    onChange={(e) => setPolicyAcknowledged(e.target.checked)}
                    className="mt-1 w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                    data-testid="policy-checkbox"
                  />
                  <span className="text-sm text-muted-foreground">
                    I have read and agree to the{' '}
                    <Link to="/cancellation-policy" target="_blank" className="text-primary hover:underline font-medium">
                      Cancellation &amp; Refund Policy
                    </Link>
                    {' '}and the{' '}
                    <Link to="/terms-conditions" target="_blank" className="text-primary hover:underline font-medium">
                      Terms &amp; Conditions
                    </Link>
                    .
                  </span>
                </label>
              </div>

              {/* CAPTCHA (RBC #20) */}
              <div className="border-t border-border pt-5 mb-5">
                <div className="bg-muted/50 rounded-lg p-4" data-testid="captcha-section">
                  <p className="text-sm font-semibold mb-3">Security Verification</p>
                  <div className="flex items-center gap-3">
                    <div className="bg-white border border-border rounded-lg px-4 py-2 font-mono text-lg font-bold select-none tracking-wider" data-testid="captcha-question">
                      {captcha.question} = ?
                    </div>
                    <Input
                      value={captchaInput}
                      onChange={(e) => verifyCaptcha(e.target.value.trim())}
                      placeholder="Answer"
                      className="w-24"
                      data-testid="captcha-input"
                    />
                    <button
                      type="button"
                      onClick={refreshCaptcha}
                      className="p-2 text-muted-foreground hover:text-foreground transition-colors"
                      title="New question"
                      data-testid="captcha-refresh"
                    >
                      <RefreshCw className="w-4 h-4" />
                    </button>
                    {captchaVerified && (
                      <Check className="w-5 h-5 text-green-600" data-testid="captcha-verified" />
                    )}
                  </div>
                </div>
              </div>

              {/* Submit Section */}
              <div className="border-t border-border pt-6">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-lg font-semibold">
                    {isCardEnabled ? 'Pay Now' : 'Amount Due on Arrival'}
                  </span>
                  <span className="text-2xl font-bold text-primary" data-testid="amount-due">BSD ${booking.total_amount.toFixed(2)}</span>
                </div>

                {!isCardEnabled ? (
                  <Button
                    onClick={handleCashOnArrival}
                    className="w-full btn-primary text-lg py-6"
                    disabled={submitting || !canSubmit}
                    data-testid="confirm-booking-btn"
                  >
                    {submitting ? (
                      'Confirming...'
                    ) : (
                      <span className="flex items-center justify-center gap-2">
                        <Check className="w-5 h-5" />
                        Confirm Booking - Pay on Arrival
                      </span>
                    )}
                  </Button>
                ) : (
                  <Button
                    onClick={handleCardPayment}
                    className="w-full btn-primary text-lg py-6"
                    disabled={submitting || !canSubmit}
                    data-testid="pay-now-btn"
                  >
                    {submitting ? 'Processing...' : `Pay BSD $${booking.total_amount.toFixed(2)}`}
                  </Button>
                )}

                <p className="text-center text-sm text-muted-foreground mt-4">
                  By {isCardEnabled ? 'completing this purchase' : 'confirming this booking'}, you agree to our{' '}
                  <Link to="/terms-conditions" className="text-primary hover:underline">Terms &amp; Conditions</Link>.
                  {!isCardEnabled && ' Full payment in BSD is required upon arrival.'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
